import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Settings } from 'lucide-react';
import { toast } from 'sonner';
import {
  User,
  type AddOrgMemberRequest,
  type UpdateOrgMemberRequest,
  type AddOrgMemberResponse,
  type UpdateOrgMemberResponse,
  type DeleteOrgMemberResponse,
  type UserType,
  type UserStatus,
  type UserClassification,
  type OrgMemberListItem
} from '@bliks-io/contracts';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrganizationSeatUsage } from '@/api/queries/useSeatUsage';
import {
  useCreateOrgMember,
  useUpdateOrgMember,
  useDeleteOrgMember,
  useResetOrgMemberSignIn
} from '@/api/mutations/useOrgMembers';
import { useOrgMemberStatusActions } from '@/pages/organization/org-members/useOrgMemberStatusActions';
import { useCrudDialogState } from '@/hooks/useCrudDialogState';
import { useCrudHandlers } from '@/hooks/useCrudHandlers';
import {
  DeleteConfirmDialog,
  Badge,
  Button,
  Page,
  PageHeader,
  PageContent,
  UserCard,
  toUserCardProps,
  DataTable,
  Skeleton,
  createActionsColumn,
  createColumnHeader,
  filterArrayIncludes,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { OrgMemberFormDialog } from '@/pages/organization/org-members/components/OrgMemberFormDialog';
import { InviteAllInactiveButton } from '@/pages/organization/org-members/components/InviteAllInactiveButton';
import { useAuthStore } from '@/stores/auth-store';
import { ContentHeader } from '@/components/content-header';
import { SeatUsageSummary } from '@/components/seat-usage/SeatUsageSummary';
import { ResetSignInConfirmDialog } from '@/components/reset-sign-in/ResetSignInConfirmDialog';
import {
  getUserStatusLabel,
  getUserStatusColorScheme,
  getUserTypeOptions,
  getUserStatusOptions,
  getUserClassificationOptions
} from '@/lib/user-utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSessionTenantType } from '@/hooks/useSessionTenantType';
import { getMemberStatusActions } from '@/components/organization/getMemberStatusActions';

export function UserManagementPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.userManagement');
  const slug = useAuthStore().slug();
  const tenantType = useSessionTenantType();
  const navigate = useNavigate();
  const { data: orgMembers, isLoading } = useOrgMembers({ withSignInReset: true });
  const { data: departments, isLoading: departmentsLoading } = useDepartments();
  // SeatUsageSummary mounts only with the table toolbar, after the member list
  // lands; reading here starts the request with the page. Unread, so it must
  // not re-render the page on every seat-usage change.
  useOrganizationSeatUsage({ notifyOnChangeProps: [] });

  // Mutations
  const createMutation = useCreateOrgMember();
  const updateMutation = useUpdateOrgMember();
  const deleteMutation = useDeleteOrgMember();
  const resetSignInMutation = useResetOrgMemberSignIn();

  const [resettingSignInMember, setResettingSignInMember] = useState<OrgMemberListItem | undefined>(
    undefined
  );

  // CRUD dialog state management
  const {
    formDialogOpen,
    editingEntity: editingMember,
    deleteDialogOpen,
    deletingEntityId: deletingMemberId,
    openCreate: handleCreate,
    openEdit: handleEdit,
    openDelete: handleDelete,
    closeFormDialog,
    closeFormDialogFrom,
    formDialogOpening,
    closeDeleteDialog
  } = useCrudDialogState<User>();

  // CRUD handlers with error handling
  const { handleFormSubmit, handleDeleteConfirm, isDeleting } = useCrudHandlers<
    User,
    AddOrgMemberRequest,
    UpdateOrgMemberRequest,
    AddOrgMemberResponse,
    UpdateOrgMemberResponse,
    DeleteOrgMemberResponse
  >({
    createMutation,
    updateMutation,
    deleteMutation,
    editingEntity: editingMember,
    deletingEntityId: deletingMemberId,
    t,
    onFormSuccess: closeFormDialog,
    onDeleteSuccess: closeDeleteDialog
  });

  // View handler
  const handleView = useCallback(
    (member: User) => {
      navigate({
        to: '/$slug/org-members/$orgMemberId',
        params: { slug, orgMemberId: member._id }
      });
    },
    [navigate, slug]
  );

  // Helper function to check if user is a department manager
  const isDepartmentManager = useCallback(
    (userId: string): boolean => {
      if (!departments) return false;
      return departments.some((d) => d.manager?._id === userId);
    },
    [departments]
  );

  const {
    handleSendInvitation,
    handleSuspend,
    handleUnsuspend,
    pendingMemberIds,
    memberDialogProps
  } = useOrgMemberStatusActions({
    dialogMemberId: editingMember?._id,
    dialogOpening: formDialogOpening,
    onDialogActionDone: closeFormDialogFrom
  });

  const handleResetSignInConfirm = async () => {
    if (!resettingSignInMember) return;

    try {
      await resetSignInMutation.mutateAsync(resettingSignInMember._id);
      toast.success(t('organization.members.resetSignInSuccess'));
      setResettingSignInMember(undefined);
    } catch (error) {
      console.error('Failed to reset sign-in:', error);
      toastRequestError(error, translateErrorMessage(error as ApiError, t));
    }
  };

  // Define columns with stable reference using useMemo - INCLUDES User Types column
  const columns = useMemo<DataTableColumnDef<OrgMemberListItem>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        meta: { label: t('common.Name'), filterType: 'text' },
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <UserCard
            user={toUserCardProps(row.original)}
            variant="name-only"
            // The table has its own status column — never mark twice.
            showLifecycleSuffix={false}
          />
        ),
        enableColumnFilter: true,
        size: 280
      },
      {
        accessorKey: 'email',
        meta: { label: t('common.Email'), filterType: 'text' },
        header: createColumnHeader(t('common.Email')),
        cell: ({ row }) => row.getValue('email'),
        enableColumnFilter: true
      },
      {
        accessorKey: 'userTypes',
        meta: {
          label: t('common.UserTypes'),
          filterType: 'select',
          filterOptions: getUserTypeOptions(t, tenantType)
        },
        header: createColumnHeader(t('common.UserTypes')),
        cell: ({ row }) => {
          const types = row.getValue('userTypes') as UserType[];
          return (
            <div className="flex flex-wrap gap-1">
              {types.map((type) => (
                <Badge key={type} variant="outline">
                  {t(`userTypes.${type}`)}
                </Badge>
              ))}
            </div>
          );
        },
        enableColumnFilter: true,
        filterFn: filterArrayIncludes,
        size: 150
      },
      {
        id: 'department',
        accessorFn: (row) => row.department?.name || '-',
        meta: { label: t('common.entityTypes.Department'), filterType: 'text' },
        header: createColumnHeader(t('common.entityTypes.Department')),
        cell: ({ row }) => row.original.department?.name || '-',
        enableColumnFilter: true
      },
      {
        id: 'isDepartmentManager',
        accessorFn: (row) => isDepartmentManager(row._id),
        meta: {
          label: t('common.DepartmentManager'),
          filterType: 'select',
          filterOptions: [
            { label: t('common.Yes'), value: 'true' },
            { label: t('common.No'), value: 'false' }
          ]
        },
        header: createColumnHeader(t('common.DepartmentManager')),
        cell: ({ row }) => {
          if (departmentsLoading) return <Skeleton className="h-5 w-10" />;
          const isManager = isDepartmentManager(row.original._id);
          return (
            <Badge colorScheme={isManager ? 'success' : 'neutral'}>
              {isManager ? t('common.Yes') : t('common.No')}
            </Badge>
          );
        },
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          return String(row.getValue(columnId)) === filterValue;
        }
      },
      {
        id: 'mainRole',
        accessorFn: (row) => row.mainRole?.name || '-',
        meta: { label: t('organization.members.mainRole'), filterType: 'text' },
        header: createColumnHeader(t('organization.members.mainRole')),
        cell: ({ row }) => row.original.mainRole?.name || '-',
        enableColumnFilter: true
      },
      {
        accessorKey: 'classification',
        meta: {
          label: t('common.Classification'),
          filterType: 'select',
          filterOptions: getUserClassificationOptions(t)
        },
        header: createColumnHeader(t('common.Classification')),
        cell: ({ row }) => {
          const classification = row.getValue('classification') as UserClassification;
          return (
            <Badge colorScheme={classification === 'external' ? 'warning' : 'neutral'}>
              {t(`userClassification.${classification}Short`)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        size: 150
      },
      {
        accessorKey: 'status',
        meta: {
          label: t('common.Status'),
          filterType: 'select',
          filterOptions: getUserStatusOptions(t)
        },
        header: createColumnHeader(t('common.Status')),
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <Badge colorScheme={getUserStatusColorScheme(status)}>
              {getUserStatusLabel(status, t)}
            </Badge>
          );
        },
        enableColumnFilter: true
      },
      createActionsColumn<OrgMemberListItem>({
        onEdit: (member) => handleEdit(member),
        onDelete: (member) => handleDelete(member._id),
        onView: (member) => handleView(member),
        getCustomActions: (member) =>
          getMemberStatusActions(
            member,
            {
              onSendInvitation: handleSendInvitation,
              onSuspend: handleSuspend,
              onUnsuspend: handleUnsuspend,
              onResetSignIn: setResettingSignInMember
            },
            t
          ),
        isRowPending: (member) => pendingMemberIds.has(member._id)
      })
    ],
    [
      t,
      tenantType,
      isDepartmentManager,
      departmentsLoading,
      handleView,
      handleEdit,
      handleDelete,
      handleSendInvitation,
      handleSuspend,
      handleUnsuspend,
      pendingMemberIds
    ]
  );

  return (
    <Page>
      <PageHeader />
      <PageContent>
        <div className="flex items-center justify-between">
          <ContentHeader
            icon={Settings}
            title={t('organizationSettings.userManagement.title', {
              defaultValue: 'User Management'
            })}
            description={t('organizationSettings.userManagement.description', {
              defaultValue: 'Manage user access, types, and permissions for your organization.'
            })}
          />
          <div className="flex items-center gap-2">
            <InviteAllInactiveButton members={orgMembers ?? []} />
            <Button variant="primary" size="medium" onClick={handleCreate}>
              <Plus className="w-4 h-4" />
              {t('organization.members.createMember')}
            </Button>
          </div>
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={orgMembers || []}
            isLoading={isLoading}
            toolbarContent={<SeatUsageSummary className="ml-auto" />}
            emptyMessage={t('organization.members.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('organization.members.search', {
              defaultValue: 'Search members...'
            })}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="userManagement"
          />
        </div>

        {/* Form Dialog - WITH User Types field */}
        <OrgMemberFormDialog
          open={formDialogOpen}
          onOpenChange={(open) => !open && closeFormDialog()}
          onSubmit={handleFormSubmit}
          member={editingMember}
          {...memberDialogProps}
          showUserTypes
        />

        <ResetSignInConfirmDialog
          person={resettingSignInMember}
          onClose={() => setResettingSignInMember(undefined)}
          onConfirm={() => void handleResetSignInConfirm()}
          isPending={resetSignInMutation.isPending}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !open && closeDeleteDialog()}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          entityPhrase={t('organization.members.deletePhrase')}
        />
      </PageContent>
    </Page>
  );
}
